/**
 * Inventario de reportes por cliente y proyecto, con selección múltiple y export PDF/Excel.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ink, studio, SearchField, FilterDock, FilterPill, FilterMeta } from './studioUi';
import { exportInventoryExcel, exportInventoryPdf } from './inventoryPdf';

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const STATUS_LABEL = {
    complete: 'Completo',
    complete_late: 'Aprobado con retrasos',
    pending: 'En curso',
    overdue: 'Vencido',
    none: 'Sin responsables',
};

function asId(value) {
    return String(value || '').trim();
}

function sameText(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

function reportType(report) {
    if (report.isDraft) return 'Borrador';
    if (report.isCustom || report.customConfig) return 'Personalizado';
    return 'Enlace';
}

function versionLabel(report) {
    const year = report.versionYear || '';
    const month = report.versionMonth ? MONTHS[Number(report.versionMonth) - 1] : '';
    return [month, year].filter(Boolean).join(' ') || '—';
}

function deadlineDate(value) {
    if (!value) return null;
    const date = typeof value.toDate === 'function'
        ? value.toDate()
        : (value.seconds ? new Date(value.seconds * 1000) : new Date(value));
    return Number.isNaN(date.getTime()) ? null : date;
}

function deadlineLabel(value) {
    const date = deadlineDate(value);
    return date ? date.toLocaleDateString('es-MX', { dateStyle: 'medium' }) : '—';
}

function reportApproval(report) {
    const total = Number(report.totalResponsibles) || 0;
    const approved = Number(report.approvedCount) || 0;
    if (!total) return { status: 'none', pct: null, approved: 0, total: 0 };
    const pct = Math.round((approved / total) * 1000) / 10;
    if (report.approvalStatus === 'complete_late') {
        return { status: 'complete_late', pct, approved, total };
    }
    if (report.approvalStatus === 'complete' || pct === 100) {
        return { status: 'complete', pct, approved, total };
    }
    if (report.approvalStatus === 'overdue') {
        return { status: 'overdue', pct, approved, total };
    }
    const due = deadlineDate(report.approvalDeadline);
    if (due && due.getTime() < Date.now() && report.approvalStatus !== 'complete' && report.approvalStatus !== 'complete_late') {
        return { status: 'overdue', pct, approved, total };
    }
    return { status: 'pending', pct, approved, total };
}

function buildApprovalStats(reports) {
    const stats = {
        reports: reports.length,
        complete: 0,
        complete_late: 0,
        pending: 0,
        overdue: 0,
        none: 0,
        approvedMarks: 0,
        totalMarks: 0,
        pct: null,
    };
    reports.forEach((report) => {
        const row = reportApproval(report);
        stats.approvedMarks += row.approved;
        stats.totalMarks += row.total;
        stats[row.status] += 1;
    });
    if (stats.totalMarks) {
        stats.pct = Math.round((stats.approvedMarks / stats.totalMarks) * 1000) / 10;
    }
    return stats;
}

function optionMatches(reportId, reportName, selectedIds, options) {
    return selectedIds.some((id) => {
        if (reportId && asId(reportId) === asId(id)) return true;
        const option = options.find((item) => asId(item.id) === asId(id));
        return option ? sameText(option.label, reportName) : false;
    });
}

function MultiCheckFilter({ label, allLabel, options, selectedIds, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const rootRef = useRef(null);
    const allSelected = options.length > 0 && selectedIds.length === options.length;
    const visible = options.filter((item) => (
        !search.trim() || String(item.label).toLowerCase().includes(search.trim().toLowerCase())
    ));
    const summary = allSelected || (!options.length && !selectedIds.length)
        ? allLabel
        : selectedIds.length === 0
            ? 'Ninguno'
            : selectedIds.length === 1
                ? (options.find((item) => item.id === selectedIds[0])?.label || '1')
                : `${selectedIds.length} de ${options.length}`;

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const toggleAll = () => {
        onChange(allSelected ? [] : options.map((item) => item.id));
    };

    const toggleOne = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter((item) => item !== id));
        else onChange([...selectedIds, id]);
    };

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                style={{
                    ...studio.filterPill,
                    ...pillLike(!allSelected && selectedIds.length > 0),
                    minWidth: 180,
                    textAlign: 'left',
                    paddingRight: 28,
                }}
            >
                {label}: {summary}
            </button>
            {open && (
                <div style={ui.menu} onMouseDown={(event) => event.stopPropagation()}>
                    <label style={ui.option}>
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                        <span>{allLabel}</span>
                    </label>
                    {options.length > 8 && (
                        <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Buscar…"
                            style={ui.search}
                        />
                    )}
                    <div style={ui.menuSplit} />
                    {visible.map((item) => (
                        <label key={item.id} style={ui.option}>
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(item.id)}
                                onChange={() => toggleOne(item.id)}
                            />
                            <span>{item.label}</span>
                        </label>
                    ))}
                    {!visible.length && (
                        <p style={{ ...studio.hint, margin: '8px 4px' }}>No hay coincidencias.</p>
                    )}
                </div>
            )}
        </div>
    );
}

function pillLike(active) {
    return {
        borderColor: active ? ink.accent : ink.lineStrong,
        backgroundColor: active ? ink.accentSoft : ink.surface,
        color: active ? ink.accentText : ink.text,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        fontFamily: 'inherit',
    };
}

const ReportInventoryPanel = ({ biblioteca = [], clients = [], projects = [] }) => {
    const [query, setQuery] = useState('');
    const [clientIds, setClientIds] = useState([]);
    const [projectIds, setProjectIds] = useState([]);
    const [kind, setKind] = useState('');
    const [exporting, setExporting] = useState('');
    const didInit = useRef(false);

    const clientOptions = useMemo(
        () => clients
            .map((item) => ({ id: asId(item.id), label: item.nombre || item.id }))
            .filter((item) => item.id)
            .sort((a, b) => a.label.localeCompare(b.label, 'es')),
        [clients]
    );

    const projectOptions = useMemo(
        () => projects
            .filter((item) => !clientIds.length || clientIds.includes(asId(item.clientId)))
            .map((item) => ({ id: asId(item.id), label: item.nombre || item.id, clientId: asId(item.clientId) }))
            .filter((item) => item.id)
            .sort((a, b) => a.label.localeCompare(b.label, 'es')),
        [projects, clientIds]
    );

    useEffect(() => {
        if (didInit.current || !clientOptions.length) return;
        const allClients = clientOptions.map((item) => item.id);
        setClientIds(allClients);
        setProjectIds(
            projects
                .filter((item) => allClients.includes(asId(item.clientId)))
                .map((item) => asId(item.id))
                .filter(Boolean)
        );
        didInit.current = true;
    }, [clientOptions, projects]);

    const changeClients = (ids) => {
        const next = ids.map(asId);
        setClientIds(next);
        setProjectIds(
            projects
                .filter((item) => next.includes(asId(item.clientId)))
                .map((item) => asId(item.id))
                .filter(Boolean)
        );
    };

    const changeProjects = (ids) => {
        setProjectIds(ids.map(asId));
    };

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const allProjectsPicked = projectOptions.length === 0
            || projectIds.length === projectOptions.length;
        return (biblioteca || []).filter((report) => {
            if (!clientIds.length) return false;
            const clientOk = optionMatches(report.clientId, report.cliente, clientIds, clientOptions);
            if (!clientOk) return false;
            if (!allProjectsPicked) {
                if (!projectIds.length) return false;
                if (!optionMatches(report.projectId, report.proyecto, projectIds, projectOptions)) {
                    return false;
                }
            }
            if (kind && reportType(report) !== kind) return false;
            if (!needle) return true;
            const hay = [
                report.reporteNombre,
                report.cliente,
                report.proyecto,
                report.enfoque,
                reportType(report),
                versionLabel(report),
            ].join(' ').toLowerCase();
            return hay.includes(needle);
        });
    }, [biblioteca, clientIds, projectIds, projectOptions, clientOptions, kind, query]);

    const groups = useMemo(() => {
        const byClient = new Map();
        filtered.forEach((report) => {
            const client = report.cliente || 'Sin cliente';
            const project = report.proyecto || 'Sin proyecto';
            if (!byClient.has(client)) byClient.set(client, new Map());
            const projectMap = byClient.get(client);
            if (!projectMap.has(project)) projectMap.set(project, []);
            projectMap.get(project).push(report);
        });
        return [...byClient.entries()]
            .sort((a, b) => a[0].localeCompare(b[0], 'es'))
            .map(([client, projectMap]) => {
                const projectsOfClient = [...projectMap.entries()]
                    .sort((a, b) => a[0].localeCompare(b[0], 'es'))
                    .map(([project, reports]) => ({
                        project,
                        reports: reports.slice().sort((a, b) =>
                            String(a.reporteNombre || '').localeCompare(String(b.reporteNombre || ''), 'es')
                        ),
                        stats: buildApprovalStats(reports),
                    }));
                const allReports = projectsOfClient.flatMap((item) => item.reports);
                return {
                    client,
                    count: allReports.length,
                    stats: buildApprovalStats(allReports),
                    projects: projectsOfClient,
                };
            });
    }, [filtered]);

    const stats = useMemo(() => buildApprovalStats(filtered), [filtered]);
    const clientCount = groups.length;
    const projectCount = groups.reduce((sum, group) => sum + group.projects.length, 0);
    const allClients = clientOptions.length > 0 && clientIds.length === clientOptions.length;
    const allProjects = projectOptions.length > 0 && projectIds.length === projectOptions.length;

    const scopeLabel = useMemo(() => {
        const clientPart = allClients
            ? 'Todos los clientes'
            : `${clientIds.length} cliente${clientIds.length === 1 ? '' : 's'}`;
        const projectPart = allProjects
            ? 'todos los proyectos'
            : `${projectIds.length} proyecto${projectIds.length === 1 ? '' : 's'}`;
        const extra = [kind, query && `búsqueda «${query}»`].filter(Boolean);
        return [clientPart, projectPart, ...extra].join(' · ');
    }, [allClients, allProjects, clientIds.length, projectIds.length, kind, query]);

    const chips = [
        query && { key: 'q', label: `“${query}”`, onClear: () => setQuery('') },
        !allClients && clientIds.length > 0 && {
            key: 'c',
            label: `${clientIds.length} cliente${clientIds.length === 1 ? '' : 's'}`,
            onClear: () => changeClients(clientOptions.map((item) => item.id)),
        },
        !allProjects && projectIds.length > 0 && {
            key: 'p',
            label: `${projectIds.length} proyecto${projectIds.length === 1 ? '' : 's'}`,
            onClear: () => changeProjects(projectOptions.map((item) => item.id)),
        },
        kind && { key: 'k', label: kind, onClear: () => setKind('') },
    ].filter(Boolean);

    const exportPayload = () => ({
        scopeLabel,
        stats,
        groups: groups.map((group) => ({
            client: group.client,
            count: group.count,
            stats: group.stats,
            projects: group.projects.map((project) => ({
                project: project.project,
                reports: project.reports.map((report) => {
                    const approval = reportApproval(report);
                    return {
                        name: report.reporteNombre || 'Sin nombre',
                        type: reportType(report),
                        approval: approval.total ? `${approval.approved} / ${approval.total}` : '—',
                        pct: approval.pct == null ? '—' : `${approval.pct}%`,
                        status: STATUS_LABEL[approval.status],
                        statusKey: approval.status,
                        deadline: deadlineLabel(report.approvalDeadline),
                    };
                }),
            })),
        })),
    });

    const runExport = (format) => {
        if (!filtered.length) return;
        setExporting(format);
        try {
            const stamp = new Date().toISOString().slice(0, 10);
            const payload = exportPayload();
            if (format === 'pdf') {
                exportInventoryPdf({ ...payload, filename: `inventario-reportes-${stamp}.pdf` });
            } else {
                exportInventoryExcel({ ...payload, filename: `inventario-reportes-${stamp}.xlsx` });
            }
        } finally {
            setExporting('');
        }
    };

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Administración</p>
                    <h2 style={studio.title}>Inventario de reportes</h2>
                    <p style={studio.subtitle}>
                        Elige uno, varios o todos los clientes y proyectos. Exporta el listado y las estadísticas de aprobación a PDF o Excel.
                    </p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{stats.reports}</span>
                        <span style={studio.statLabel}>Reportes</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{stats.pct == null ? '—' : `${stats.pct}%`}</span>
                        <span style={studio.statLabel}>Aprobación</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{stats.complete}</span>
                        <span style={studio.statLabel}>Completos</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={{ ...studio.statValue, color: stats.complete_late ? '#e65100' : undefined }}>{stats.complete_late || 0}</span>
                        <span style={studio.statLabel}>Con retrasos</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{stats.pending}</span>
                        <span style={studio.statLabel}>En curso</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{stats.overdue}</span>
                        <span style={studio.statLabel}>Vencidos</span>
                    </div>
                </div>
            </div>

            <FilterDock>
                <SearchField
                    value={query}
                    onChange={setQuery}
                    placeholder="Buscar reporte…"
                />
                <div style={{ ...studio.filterPills, alignItems: 'center' }}>
                    <MultiCheckFilter
                        label="Clientes"
                        allLabel="Todos los clientes"
                        options={clientOptions}
                        selectedIds={clientIds}
                        onChange={changeClients}
                    />
                    <MultiCheckFilter
                        label="Proyectos"
                        allLabel="Todos los proyectos"
                        options={projectOptions}
                        selectedIds={projectIds}
                        onChange={changeProjects}
                    />
                    <FilterPill value={kind} emptyLabel="Tipo" onChange={setKind}>
                        <option value="Enlace">Enlace</option>
                        <option value="Personalizado">Personalizado</option>
                        <option value="Borrador">Borrador</option>
                    </FilterPill>
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <button
                            type="button"
                            style={{
                                ...studio.ghostBtn,
                                opacity: filtered.length && !exporting ? 1 : 0.5,
                            }}
                            onClick={() => runExport('xlsx')}
                            disabled={!filtered.length || Boolean(exporting)}
                        >
                            {exporting === 'xlsx' ? 'Generando…' : 'Exportar Excel'}
                        </button>
                        <button
                            type="button"
                            style={{
                                ...studio.primaryBtn,
                                opacity: filtered.length && !exporting ? 1 : 0.5,
                            }}
                            onClick={() => runExport('pdf')}
                            disabled={!filtered.length || Boolean(exporting)}
                        >
                            {exporting === 'pdf' ? 'Generando…' : 'Exportar PDF'}
                        </button>
                    </div>
                </div>
            </FilterDock>

            <FilterMeta
                summary={`${stats.reports} reportes · ${clientCount} clientes · ${projectCount} proyectos · ${stats.approvedMarks}/${stats.totalMarks || 0} marcas aprobadas`}
                chips={chips}
                onClearAll={chips.length ? () => {
                    setQuery('');
                    setKind('');
                    changeClients(clientOptions.map((item) => item.id));
                } : undefined}
            />

            <div style={studio.card}>
                {!clientIds.length ? (
                    <p style={{ ...studio.hint, margin: '12px 4px' }}>Selecciona al menos un cliente para ver el inventario.</p>
                ) : !filtered.length ? (
                    <p style={{ ...studio.hint, margin: '12px 4px' }}>No hay reportes con esa selección.</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={studio.table}>
                            <thead>
                                <tr>
                                    <th style={studio.th}>Proyecto</th>
                                    <th style={studio.th}>Reporte</th>
                                    <th style={studio.th}>Tipo</th>
                                    <th style={studio.th}>Aprobación</th>
                                    <th style={studio.th}>%</th>
                                    <th style={studio.th}>Estado</th>
                                    <th style={studio.th}>Deadline</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((group) => (
                                    <React.Fragment key={group.client}>
                                        <tr>
                                            <td colSpan={7} style={ui.clientHead}>
                                                {group.client}
                                                <span style={ui.clientCount}>
                                                    {group.count} · {group.stats.pct == null ? 'sin responsables' : `${group.stats.pct}%`} · {group.stats.complete} completos / {group.stats.complete_late || 0} con retrasos / {group.stats.pending} en curso / {group.stats.overdue} vencidos
                                                </span>
                                            </td>
                                        </tr>
                                        {group.projects.map((project) => (
                                            project.reports.map((report, index) => {
                                                const approval = reportApproval(report);
                                                return (
                                                    <tr key={report.id}>
                                                        <td style={studio.td}>{index === 0 ? project.project : ''}</td>
                                                        <td style={{ ...studio.td, fontWeight: 600 }}>{report.reporteNombre || 'Sin nombre'}</td>
                                                        <td style={studio.td}>{reportType(report)}</td>
                                                        <td style={studio.td}>
                                                            {approval.total ? `${approval.approved} / ${approval.total}` : '—'}
                                                        </td>
                                                        <td style={studio.td}>{approval.pct == null ? '—' : `${approval.pct}%`}</td>
                                                        <td style={{ ...studio.td, color: statusColor(approval.status), fontWeight: 600 }}>
                                                            {STATUS_LABEL[approval.status]}
                                                        </td>
                                                        <td style={studio.td}>{deadlineLabel(report.approvalDeadline)}</td>
                                                    </tr>
                                                );
                                            })
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

function statusColor(status) {
    if (status === 'complete') return '#137333';
    if (status === 'complete_late') return '#e65100';
    if (status === 'overdue') return ink.danger;
    if (status === 'pending') return '#e37400';
    return ink.faint;
}

const ui = {
    menu: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        zIndex: 20,
        minWidth: 260,
        maxHeight: 320,
        overflowY: 'auto',
        padding: 8,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(28, 29, 33, 0.14)',
    },
    menuSplit: {
        height: 1,
        backgroundColor: ink.line,
        margin: '6px 4px',
    },
    search: {
        ...studio.controlCompact,
        width: '100%',
        marginTop: 6,
        boxSizing: 'border-box',
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 8px',
        borderRadius: 8,
        fontSize: '0.88rem',
        color: ink.text,
        cursor: 'pointer',
    },
    clientHead: {
        ...studio.td,
        backgroundColor: ink.wash,
        color: ink.accentText,
        fontWeight: 700,
        fontSize: '0.82rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        paddingTop: 14,
        paddingBottom: 14,
    },
    clientCount: {
        marginLeft: 10,
        fontSize: '0.72rem',
        fontWeight: 600,
        color: ink.muted,
        letterSpacing: 0,
        textTransform: 'none',
    },
};

export default ReportInventoryPanel;
